import React, { useState, useEffect, useCallback } from 'react';
import { FaEdit, FaUndo, FaCheck, FaTimes, FaEnvelope } from 'react-icons/fa';
import { toast } from 'react-toastify';
import EmailTemplateService, {
  EmailTemplateType,
  EmailTemplateListItem,
  EMAIL_TEMPLATE_TYPE_OPTIONS,
} from '../services/EmailTemplateService';
import EmailTemplateModal from './EmailTemplateModal';

export default function EmailTemplatesPage(): React.JSX.Element {
  const [templates, setTemplates] = useState<EmailTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState<EmailTemplateType | null>(null);
  const [resetting, setResetting] = useState<EmailTemplateType | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await EmailTemplateService.list();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load email templates:', err);
      // If API doesn't exist yet, show default templates
      const defaultTemplates: EmailTemplateListItem[] = EMAIL_TEMPLATE_TYPE_OPTIONS.map((opt) => ({
        id: null,
        type: opt.value,
        language: 'de',
        subject_template: getDefaultSubject(opt.value),
        is_customized: false,
        is_active: true,
        updated_at: null,
      }));
      setTemplates(defaultTemplates);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleReset = async (type: EmailTemplateType) => {
    if (!window.confirm(`Template "${EmailTemplateService.getTypeLabel(type)}" auf Standard zurücksetzen?`)) {
      return;
    }

    setResetting(type);
    try {
      await EmailTemplateService.resetToDefault(type);
      toast.success('Template zurückgesetzt');
      loadTemplates();
    } catch (err) {
      console.error('Failed to reset template:', err);
      toast.error('Zurücksetzen fehlgeschlagen');
    } finally {
      setResetting(null);
    }
  };

  const handleModalClose = () => {
    setEditingType(null);
  };

  const handleModalSave = () => {
    setEditingType(null);
    loadTemplates();
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 600, color: '#021a4a' }}>
          E-Mail-Vorlagen
        </h2>
        <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
          Passen Sie die E-Mail-Texte für Angebote, Rechnungen und Mahnungen an.
        </p>
      </div>

      {/* Templates List */}
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e0e3ef',
          overflow: 'hidden',
        }}
      >
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 120px 100px 120px',
            gap: '16px',
            padding: '12px 20px',
            background: '#f8f9fc',
            borderBottom: '1px solid #e0e3ef',
            fontSize: '12px',
            fontWeight: 600,
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          <div>Typ</div>
          <div>Status</div>
          <div>Aktiv</div>
          <div>Aktionen</div>
        </div>

        {/* Template Rows */}
        {templates.map((template) => (
          <div
            key={template.type}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 120px 100px 120px',
              gap: '16px',
              padding: '16px 20px',
              borderBottom: '1px solid #f0f1f5',
              alignItems: 'center',
            }}
          >
            {/* Type & Description */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    background: getTypeColor(template.type).bg,
                    color: getTypeColor(template.type).text,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FaEnvelope size={14} />
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#021a4a', fontSize: '14px' }}>
                    {EmailTemplateService.getTypeLabel(template.type)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                    {EmailTemplateService.getTypeDescription(template.type)}
                  </div>
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500,
                  background: template.is_customized ? '#dbeafe' : '#f3f4f6',
                  color: template.is_customized ? '#1e40af' : '#6b7280',
                }}
              >
                {template.is_customized ? 'Angepasst' : 'Standard'}
              </span>
            </div>

            {/* Active */}
            <div>
              {template.is_active ? (
                <span style={{ color: '#16a34a', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <FaCheck size={12} /> Aktiv
                </span>
              ) : (
                <span style={{ color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <FaTimes size={12} /> Inaktiv
                </span>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setEditingType(template.type)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #e0e3ef',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#374151',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                title="Bearbeiten"
              >
                <FaEdit size={12} />
              </button>
              {template.is_customized && (
                <button
                  onClick={() => handleReset(template.type)}
                  disabled={resetting === template.type}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    background: '#fef2f2',
                    color: '#dc2626',
                    fontSize: '13px',
                    cursor: resetting === template.type ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: resetting === template.type ? 0.6 : 1,
                  }}
                  title="Auf Standard zurücksetzen"
                >
                  <FaUndo size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editingType && (
        <EmailTemplateModal
          type={editingType}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
}

function getDefaultSubject(type: EmailTemplateType): string {
  switch (type) {
    case 'OFFER':
      return 'Angebot {{ offer.offer_number }}: {{ offer.title }}';
    case 'ORDER_CONFIRMATION':
      return 'Auftragsbestätigung {{ offer.order_confirmation_number }}';
    case 'INVOICE':
      return 'Rechnung {{ invoice.invoice_number }}';
    case 'REMINDER':
      return 'Zahlungserinnerung: Rechnung {{ invoice.invoice_number }}';
    case 'DUNNING_1':
      return '1. Mahnung: Rechnung {{ invoice.invoice_number }}';
    case 'DUNNING_2':
      return '2. Mahnung: Rechnung {{ invoice.invoice_number }}';
    case 'DUNNING_3':
      return 'Letzte Mahnung: Rechnung {{ invoice.invoice_number }}';
    default:
      return '';
  }
}

function getTypeColor(type: EmailTemplateType): { bg: string; text: string } {
  switch (type) {
    case 'OFFER':
      return { bg: '#dbeafe', text: '#1e40af' };
    case 'ORDER_CONFIRMATION':
      return { bg: '#d1fae5', text: '#065f46' };
    case 'INVOICE':
      return { bg: '#e0e7ff', text: '#3730a3' };
    case 'REMINDER':
      return { bg: '#fef3c7', text: '#92400e' };
    case 'DUNNING_1':
      return { bg: '#fed7aa', text: '#c2410c' };
    case 'DUNNING_2':
      return { bg: '#fecaca', text: '#dc2626' };
    case 'DUNNING_3':
      return { bg: '#fca5a5', text: '#991b1b' };
    default:
      return { bg: '#f3f4f6', text: '#374151' };
  }
}
